import { Plus, Trash2, ArrowUp, ArrowDown, ChevronDown, GripVertical, RotateCcw } from "lucide-react";
import { Input, Select, Toggle } from "./fields";

// Réglages d'apparence du menu horizontal (appliqués au site public via nav.settings).
// Valeurs par défaut = le rendu actuel du site (12px / 20px / 80px).
export const NAV_DEFAULTS = { fontSize: 13, gap: 22, height: 80 };

const Slider = ({ label, value, min, max, step = 1, unit = "px", onChange, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="flex justify-between text-[10px] tracking-[0.2em] uppercase text-secondary2">
      <span>{label}</span><span className="text-electric">{value}{unit}</span>
    </span>
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))}
      className="mt-1.5 w-full accent-electric" data-testid={`${testid}-input`} />
  </label>
);

// Aperçu immédiat (immobile, avant enregistrement) du rendu ordinateur.
const NavPreview = ({ items, cta, st }) => (
  <div className="rounded-lg border border-electric/20 overflow-hidden" data-testid="nav-appearance-preview">
    <div className="text-[9px] tracking-[0.25em] uppercase text-secondary2 bg-[#080808] px-3 py-1.5">Aperçu ordinateur — mis à jour en direct</div>
    <div className="bg-black/80 backdrop-blur px-4 flex items-center gap-4" style={{ height: st.height }}>
      <img src="/karams-logo.png" alt="" className="h-9 w-auto shrink-0" />
      <div className="flex-1 flex items-center justify-center min-w-0 overflow-hidden" style={{ gap: st.gap }}>
        {items.filter((i) => i.visible !== false).map((it) => (
          <span key={it.id} className="uppercase text-secondary2 whitespace-nowrap" style={{ fontSize: st.fontSize, letterSpacing: "0.12em" }}>{it.label}</span>
        ))}
      </div>
      {cta.visible !== false && cta.label && (
        <span className="shrink-0 rounded-full border border-white/15 text-white px-3 py-1.5 uppercase" style={{ fontSize: Math.max(9, st.fontSize - 1) }}>{cta.label}</span>
      )}
    </div>
    <p className="text-[10px] text-secondary2 bg-[#080808] px-3 py-1.5">Sur téléphone et fenêtres étroites (&lt; 1024px), le menu hamburger reste automatique — aucun lien ne disparaît.</p>
  </div>
);

const uid = () => `nav-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`;

const SECTIONS = [
  { value: "hero", label: "Accueil (haut)" }, { value: "mission", label: "Mission" },
  { value: "association", label: "L'Association" }, { value: "activites", label: "Nos activités" },
  { value: "realisations", label: "Réalisations" }, { value: "festival", label: "Festival" },
  { value: "news", label: "Actualités" }, { value: "formations", label: "Formations" },
  { value: "team", label: "Équipe" }, { value: "contact", label: "Contact" },
];

const TYPES = (allowGroup) => [
  { value: "section", label: "Section de la page d'accueil" },
  { value: "page", label: "Page personnalisée" },
  { value: "path", label: "Lien interne (ex: /formations)" },
  { value: "url", label: "Lien externe (nouvelle fenêtre)" },
  ...(allowGroup ? [{ value: "group", label: "Menu déroulant (sous-menu)" }] : []),
];

const TargetField = ({ item, onPatch, pages }) => {
  if (item.type === "group") return <div className="text-xs text-secondary2 self-center">Ajoutez des sous-liens ci-dessous ↓</div>;
  if (item.type === "section") return <Select label="Section cible" value={item.target} onChange={(v) => onPatch({ target: v })} options={SECTIONS} />;
  if (item.type === "page") return <Select label="Page cible" value={item.target} onChange={(v) => onPatch({ target: v })} options={[{ value: "", label: "— choisir —" }, ...pages.map((p) => ({ value: p.slug, label: p.title || p.slug }))]} />;
  if (item.type === "path") return <Input label="Chemin interne" value={item.target} onChange={(v) => onPatch({ target: v })} placeholder="/formations" />;
  return <Input label="URL externe" value={item.target} onChange={(v) => onPatch({ target: v })} placeholder="https://..." />;
};

const ChildRow = ({ child, onPatch, onDelete, move, pages }) => (
  <div className="rounded border border-white/10 bg-[#050505] p-3 space-y-3">
    <div className="flex items-center justify-between">
      <span className="inline-flex items-center gap-2 text-[10px] text-secondary2"><GripVertical size={12} /> Sous-lien</span>
      <div className="flex items-center gap-2">
        <button onClick={() => move(-1)} className="text-white/40 hover:text-electric"><ArrowUp size={13} /></button>
        <button onClick={() => move(1)} className="text-white/40 hover:text-electric"><ArrowDown size={13} /></button>
        <button onClick={onDelete} className="text-red-400/70 hover:text-red-400"><Trash2 size={13} /></button>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <Input label="Libellé" value={child.label} onChange={(v) => onPatch({ label: v })} />
      <Select label="Type" value={child.type} onChange={(v) => onPatch({ type: v, target: "" })} options={TYPES(false)} />
      <TargetField item={child} onPatch={onPatch} pages={pages} />
    </div>
    <Toggle label="Visible" checked={child.visible !== false} onChange={(v) => onPatch({ visible: v })} />
  </div>
);

export const AdminNav = ({ value = {}, pages = [], onChange }) => {
  const items = value.items || [];
  const cta = value.cta || { label: "", type: "section", target: "contact", visible: true };
  const st = { ...NAV_DEFAULTS, ...(value.settings || {}) };
  const setItems = (arr) => onChange({ ...value, items: arr });
  const setCta = (p) => onChange({ ...value, cta: { ...cta, ...p } });
  const setSetting = (k, v) => onChange({ ...value, settings: { ...(value.settings || {}), [k]: v } });
  const resetSettings = () => onChange({ ...value, settings: {} });
  const patch = (i, p) => { const c = [...items]; c[i] = { ...c[i], ...p }; setItems(c); };
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= items.length) return; const c = [...items]; [c[i], c[j]] = [c[j], c[i]]; setItems(c); };
  const add = () => setItems([...items, { id: uid(), label: "Nouveau lien", type: "section", target: "hero", visible: true, children: [] }]);

  const setChildren = (i, ch) => patch(i, { children: ch });
  const addChild = (i) => setChildren(i, [...(items[i].children || []), { id: uid(), label: "Sous-lien", type: "section", target: "hero", visible: true }]);
  const patchChild = (i, ci, p) => { const ch = [...(items[i].children || [])]; ch[ci] = { ...ch[ci], ...p }; setChildren(i, ch); };
  const moveChild = (i, ci, dir) => { const j = ci + dir; const ch = [...(items[i].children || [])]; if (j < 0 || j >= ch.length) return; [ch[ci], ch[j]] = [ch[j], ch[ci]]; setChildren(i, ch); };

  return (
    <div className="space-y-6" data-testid="admin-nav-editor">
      <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 text-sm text-secondary2">
        Contrôlez entièrement votre menu : renommez, réordonnez, masquez, ajoutez des liens (vers une section, une page, un lien interne ou externe) et créez des menus déroulants. Les changements sont enregistrés en brouillon puis publiés.
      </div>

      <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-4 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Apparence du menu (ordinateur)</span>
          <button onClick={resetSettings} data-testid="nav-settings-reset" className="inline-flex items-center gap-1 text-[11px] text-white/50 hover:text-electric"><RotateCcw size={12} /> Réinitialiser</button>
        </div>
        <NavPreview items={items} cta={cta} st={st} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Slider label="Taille des textes" value={st.fontSize} min={10} max={16} onChange={(v) => setSetting("fontSize", v)} testid="nav-setting-fontsize" />
          <Slider label="Espacement entre rubriques" value={st.gap} min={8} max={40} onChange={(v) => setSetting("gap", v)} testid="nav-setting-gap" />
          <Slider label="Hauteur de la barre" value={st.height} min={56} max={104} onChange={(v) => setSetting("height", v)} testid="nav-setting-height" />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Liens du menu</span>
        <button onClick={add} data-testid="admin-add-nav" className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-electric"><Plus size={14} /> Ajouter un lien</button>
      </div>

      {items.map((it, i) => (
        <div key={it.id || i} data-testid={`admin-nav-item-${i}`} className="rounded-lg border border-white/10 p-4 bg-[#0a0a0a] space-y-4">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-[10px] text-secondary2">
              <GripVertical size={13} /> #{i + 1}{it.type === "group" && <span className="inline-flex items-center gap-1 text-electric"><ChevronDown size={11} /> déroulant</span>}
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => move(i, -1)} data-testid={`admin-nav-up-${i}`} className="text-white/40 hover:text-electric"><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} data-testid={`admin-nav-down-${i}`} className="text-white/40 hover:text-electric"><ArrowDown size={14} /></button>
              <button onClick={() => setItems(items.filter((_, x) => x !== i))} data-testid={`admin-nav-delete-${i}`} className="text-red-400/70 hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label="Libellé" value={it.label} onChange={(v) => patch(i, { label: v })} />
            <Select label="Type" value={it.type} onChange={(v) => patch(i, { type: v, target: v === "group" ? "" : it.target, children: v === "group" ? (it.children || []) : [] })} options={TYPES(true)} />
            <TargetField item={it} onPatch={(p) => patch(i, p)} pages={pages} />
          </div>
          <Toggle label="Visible dans le menu" checked={it.visible !== false} onChange={(v) => patch(i, { visible: v })} />

          {it.type === "group" && (
            <div className="border-t border-white/10 pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Sous-liens du menu déroulant</span>
                <button onClick={() => addChild(i)} data-testid={`admin-add-subnav-${i}`} className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-electric"><Plus size={13} /> Ajouter un sous-lien</button>
              </div>
              {(it.children || []).length === 0 && <p className="text-xs text-secondary2">Aucun sous-lien. Ajoutez-en pour créer le menu déroulant.</p>}
              {(it.children || []).map((ch, ci) => (
                <ChildRow key={ch.id || ci} child={ch} pages={pages}
                  onPatch={(p) => patchChild(i, ci, p)}
                  onDelete={() => setChildren(i, it.children.filter((_, x) => x !== ci))}
                  move={(dir) => moveChild(i, ci, dir)} />
              ))}
            </div>
          )}
        </div>
      ))}

      <div className="border-t border-white/10 pt-6 space-y-3">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Bouton d'action (à droite du menu)</span>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input label="Libellé du bouton" value={cta.label} onChange={(v) => setCta({ label: v })} />
          <Select label="Type" value={cta.type} onChange={(v) => setCta({ type: v, target: "" })} options={TYPES(false)} />
          <TargetField item={cta} onPatch={setCta} pages={pages} />
        </div>
        <Toggle label="Afficher le bouton" checked={cta.visible !== false} onChange={(v) => setCta({ visible: v })} />
      </div>
    </div>
  );
};
